import { Role } from '@bioliberty/core';
import { HttpStatus } from '@bioliberty/http';
import { InviteUserBody } from '@bioliberty/lifemind';
import { Info } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TagPicker } from 'rsuite';
import z from 'zod';
import { useInviteUserMutation } from '../../api';
import { useAuthentication } from '../../auth';
import { Button } from '../../components/Button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/Input/Select';
import { ErrorMessage, InputLabel, TextInput } from '../../components/Input/TextInput';
import { Switch } from '../../components/Switch';
import { nameRegExp } from '../../constants';
import { useActiveSubtenants } from '../../hooks/useActiveSubtenants';
import { useToaster } from '../../hooks/useToaster';
import { useValidator } from '../../hooks/useValidator';
import { addUser as addAdminViewUser } from '../../stores/slices/admin-view';
import { isSuperAdmin } from '../../util/auth';
import { getRoleLabel } from '../utils';

type InviteUserModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const InviteUserModal = ({ open, onOpenChange }: InviteUserModalProps) => {
  const currentUser = useAuthentication();
  const activeSubtenants = useActiveSubtenants();
  const toast = useToaster();
  const [inviteUser, { isLoading: isUpdating }] = useInviteUserMutation();
  const dispatch = useDispatch();

  const [userInvite, setUserInvite] = useState<InviteUserBody>({
    email: '',
    firstName: '',
    lastName: '',
    roles: [Role.Clinician],
    hidden: false
  });

  const clinicOptions = useMemo(
    () => activeSubtenants?.map(({ name, label }) => ({ value: name, label })) ?? [],
    [activeSubtenants]
  );

  const { validate, errors } = useValidator(
    z
      .object({
        email: z.string().email('Please enter a valid email address').min(1, 'An email is required'),
        firstName: z.string().regex(nameRegExp, 'Please enter a valid name').min(1, 'A first name is required'),
        lastName: z.string().regex(nameRegExp, 'Please enter a valid name').min(1, 'A last name is required'),
        roles: z.array(z.enum(Role, { message: 'Please select a valid role' })).min(1, 'A role is required'),
        subtenants: z.array(z.string()).optional()
      })
      .refine(({ roles, subtenants }) => !clinicOptions.length || !!subtenants?.length || isSuperAdmin({ roles }), {
        message: 'At least one clinic assignment is required',
        path: ['subtenants']
      })
  );

  const onChange = (property: string, value?: string | string[] | boolean) => {
    setUserInvite({ ...userInvite, [property]: value });
  };

  const submit = useCallback(async () => {
    if (!validate(userInvite)) {
      return;
    }

    try {
      const invitedUser = await inviteUser(userInvite).unwrap();
      dispatch(addAdminViewUser(invitedUser));
      toast.success(`An invitation has been sent to ${userInvite.email}.`);
      onOpenChange(false);
    } catch (error) {
      const status = (error as { status: number }).status;
      if (status === HttpStatus.Conflict) {
        toast.error('Failed to invite as a user with the email address already exists.');
      } else {
        toast.error('Failed to invite user. Please try again.');
      }
    }
  }, [dispatch, inviteUser, onOpenChange, toast, userInvite, validate]);

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        onOpenChange(open);
        setUserInvite({
          email: '',
          firstName: '',
          lastName: '',
          roles: [Role.Clinician],
          hidden: false
        });
      }}
    >
      <DialogContent
        className="sm:max-w-[800px]"
        data-testid="invite-user-modal"
        // Necessary workaround to fix scrolling within the clinic picker:
        // https://github.com/radix-ui/primitives/issues/1159
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
        </DialogHeader>

        <div className="flex gap-5">
          <div className="mt-2.5 flex w-full">
            <TextInput
              label="Email address*"
              onChange={(value) => onChange('email', value)}
              errorMessage={errors.email}
            />
          </div>
          <div className="mt-2.5 flex w-full flex-col">
            <InputLabel htmlFor="input-field">Role*</InputLabel>
            <Select
              onValueChange={(value) => {
                setUserInvite({
                  ...userInvite,
                  roles: [value],
                  subtenants: value === Role.SuperAdmin ? undefined : userInvite.subtenants
                });
              }}
              value={userInvite.roles.length > 0 ? userInvite.roles[0] : Role.Clinician}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {isSuperAdmin(currentUser) && (
                  <SelectItem value={Role.SuperAdmin}>{getRoleLabel(Role.SuperAdmin)}</SelectItem>
                )}
                <SelectItem value={Role.Admin}>{getRoleLabel(Role.Admin)}</SelectItem>
                <SelectItem value={Role.Clinician}>{getRoleLabel(Role.Clinician)}</SelectItem>
              </SelectContent>
            </Select>
            <ErrorMessage>{errors.roles}</ErrorMessage>
          </div>
        </div>
        <div className="flex gap-5">
          <div className="flex w-full">
            <TextInput
              label="First name*"
              onChange={(value) => onChange('firstName', value)}
              errorMessage={errors.firstName}
            />
          </div>
          <div className="flex w-full">
            <TextInput
              label="Last name*"
              onChange={(value) => onChange('lastName', value)}
              errorMessage={errors.lastName}
            />
          </div>
        </div>

        {!!clinicOptions.length && (
          <div>
            <InputLabel>Clinic Assignments*</InputLabel>
            {!isSuperAdmin(userInvite) && (
              <TagPicker
                data={clinicOptions}
                value={userInvite.subtenants}
                onChange={(value) => onChange('subtenants', value)}
                placeholder="Select one or more clinics"
                className="dark w-full"
                menuStyle={{ overflow: 'auto' }}
              />
            )}
            {isSuperAdmin(userInvite) && (
              <div className="flex min-h-[36px] items-center">
                <Icon type={Info} className="me-2 inline text-xs" />
                This user role has access to all clinics.
              </div>
            )}
            {errors.subtenants && <ErrorMessage>{errors.subtenants}</ErrorMessage>}
          </div>
        )}

        {currentUser.hidden && (
          <div className="mb-2.5">
            <InputLabel>Visibility*</InputLabel>
            <Switch
              checked={!userInvite.hidden}
              onChange={(value) => onChange('hidden', !value)}
              checkedChildren="Visible to other users"
              unCheckedChildren="Hidden from other users"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="accent" onClick={() => submit()} disabled={isUpdating} isLoading={isUpdating}>
            Invite
          </Button>
          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
